import { test as testConstants, roleuser } from '@edulastic/constants'
import { stackedTranslationStringToBoolMap } from '../../../assessment/components/Common/StackedTranslator/constants'
import { isEditAllowed } from './constants'
import { IconInfo } from '@edulastic/icons'
import { Popover } from 'antd'
import styled from 'styled-components'

const { accommodationsSettings, accommodations } = testConstants

const StyledInfoIcon = styled(IconInfo)`
  margin: -3px 4px;
  cursor: pointer;
`

const {
  immersiveReader,
  speechToText,
  textToSpeech,
  stackedTranslationPermission,
  textToSpeechForPassages,
  multiLanguageEnabled,
} = accommodationsSettings

export const getAccommodationsSettingOptions = ({
  i18translate,
  features,
  districtTestSettings,
  userRole,
  disabled,
  premium,
  isTestlet,
  allowedToSelectMultiLanguage,
  showImmersiveReader,
  showSpeechToText,
  showTextToSpeech,
  stackedTranslation,
  isDocBased,
  showTtsForPassages = true,
  multiLanguageEnabled: isMultiLanguageEnabled,
  isAssignFlow = false,
}) => {
  const isAccommodationEditAllowed =
    isEditAllowed({
      testSettings: districtTestSettings,
    }) || userRole !== roleuser.TEACHER

  const accommodationsData = {
    [immersiveReader.key]: {
      settings: [
        {
          key: immersiveReader.key,
          value: showImmersiveReader,
          title: accommodations[immersiveReader.key],
          description: i18translate(
            'accommodationsSettings.immersiveReader.description'
          ),
          id: immersiveReader.id,
          isEnabled:
            features.canUseImmersiveReader &&
            !isDocBased &&
            isAccommodationEditAllowed, // IR doesn't work in Doc based so disabling for it
          displaySetting: true,
          hasStudentLevelOption: true,
          hasEnableOption: true,
        },
      ],
    },
    [speechToText.key]: {
      settings: [
        {
          key: speechToText.key,
          value: showSpeechToText,
          title: accommodations[speechToText.key],
          description: i18translate(
            'accommodationsSettings.speechToText.description'
          ),
          id: speechToText.id,
          isEnabled: features.speechToText && isAccommodationEditAllowed,
          displaySetting: districtTestSettings?.enableSpeechToText,
          hasStudentLevelOption: true,
          hasEnableOption: true,
        },
      ],
    },
    [textToSpeech.key]: {
      settings: [
        {
          key: textToSpeech.key,
          value: showTextToSpeech,
          title: 'For All Item Types',
          id: textToSpeech.id,
          isEnabled: features?.textToSpeech && isAccommodationEditAllowed,
          displaySetting: true,
          hasStudentLevelOption: true,
          hasEnableOption: true,
          description: i18translate(
            'accommodationsSettings.textToSpeech.overallDescription'
          ),
          hideDescriptionInTestSettings: true,
        },
        {
          key: textToSpeechForPassages.key,
          value: showTtsForPassages,
          title: 'Enable TTS on passage',
          id: textToSpeechForPassages.id,
          isEnabled: !disabled && premium && isAccommodationEditAllowed,
          displaySetting: !isDocBased && !isTestlet,
          hasStudentLevelOption: true,
          hasEnableOption: false,
          doNotAllowUndefined: true,
          description: i18translate(
            'accommodationsSettings.textToSpeech.overallDescription'
          ),
          hideDescriptionInTestSettings: true,
        },
      ],
      description: i18translate(
        'accommodationsSettings.textToSpeech.overallDescription'
      ),
      title: accommodations[textToSpeech.key],
    },
    [stackedTranslationPermission.key]: {
      settings: [
        {
          key: stackedTranslationPermission.key,
          value: stackedTranslationStringToBoolMap[stackedTranslation],
          title: accommodations[stackedTranslationPermission.key],
          description: i18translate(
            'accommodationsSettings.stackedTranslation.description'
          ),
          id: stackedTranslationPermission.id,
          isEnabled:
            features?.canUseStackedTranslation && isAccommodationEditAllowed,
          displaySetting: true,
          hasStudentLevelOption: true,
          hasEnableOption: true,
        },
      ],
    },
    [multiLanguageEnabled.key]: {
      settings: [
        {
          key: multiLanguageEnabled.key,
          value: isMultiLanguageEnabled,
          title: accommodations[multiLanguageEnabled.key],
          description: (
            <>
              {i18translate('accommodationsSettings.multiLanguage.description')}
              {isAssignFlow && (
                <Popover
                  placement="bottom"
                  content={
                    <div style={{ maxWidth: '438px' }}>
                      {i18translate(
                        'accommodationsSettings.multiLanguage.infoMessage'
                      )}
                    </div>
                  }
                >
                  <StyledInfoIcon />
                </Popover>
              )}
            </>
          ),
          id: multiLanguageEnabled.id,
          isEnabled: !disabled && isAccommodationEditAllowed,
          displaySetting: allowedToSelectMultiLanguage,
          hasStudentLevelOption: true,
          hasEnableOption: false,
          doNotAllowUndefined: true,
        },
      ],
    },
  }

  const filteredAccommodationsData = Object.keys(accommodationsData).reduce(
    (acc, key) => {
      const accommodationSettings = accommodationsData[key].settings
      const updatedAccommodationSettings = []

      accommodationSettings.forEach((accommodationSetting) => {
        if (accommodationSetting.displaySetting) {
          updatedAccommodationSettings.push(accommodationSetting)
        }
      })

      if (updatedAccommodationSettings.length > 0) {
        acc[key] = {
          ...accommodationsData[key],
          settings: updatedAccommodationSettings,
        }
      }
      return acc
    },
    {}
  )

  return {
    accommodationsData: filteredAccommodationsData,
    accommodationSettingsData: Object.values(
      filteredAccommodationsData
    ).flatMap((accommodation) => accommodation.settings),
  }
}
